import { Directive, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { GlobalVariablesSelectComponent } from '../../pages/global-variables/global-variables-select/global-variables-select.component';
import { GlobalVariablesService } from '@services/global-variables/global-variables.service';
import { MessengerService } from '@services/messenger/messenger.service';

@Directive({
  selector: '[globalVariable]',
  standalone: true,
  providers: [GlobalVariablesService]
})
export class GlobalVariableDirective implements OnInit {
  @Input() targetObject: any;
  @Input() fieldName: string = '';

  @Input() globalVariable: any;
  @Output() globalVariableChange = new EventEmitter<any>();

  glo: any;
  icon: any;
  nameElement: any;

  constructor(private el: ElementRef, private renderer: Renderer2, private dialog: MatDialog, private varService: GlobalVariablesService, private ms: MessengerService) { }

  async ngOnInit(): Promise<void> {
    const privileges = this.ms.$superadminPrivileges.getValue();
    if (!(privileges || []).find(el => el.privilegeName === 'superadmin')) {
      return;
    }
    const targetId = this.targetObject?._id?.toString();
    this.glo = (await this.varService.find(0, 1, { 'connections.field': this.fieldName, 'connections.documentId': targetId }))?.data?.[0];

    if (this.glo) {
      this.setNestedValue(this.targetObject, this.fieldName, this.getValue(this.glo));
    }

    const inputEl = this.el.nativeElement;
    const parent = inputEl.parentNode;

    // 1. Outer container (Vertical layout)
    const container = this.renderer.createElement('div');
    this.renderer.addClass(container, 'glo');
    this.renderer.setStyle(container, 'display', 'inline-flex');
    this.renderer.setStyle(container, 'flex-direction', 'column');
    this.renderer.setStyle(container, 'position', 'relative');
    this.renderer.setStyle(container, 'vertical-align', 'top');

    // 2. Control Row (Input + Button side-by-side)
    const controlRow = this.renderer.createElement('div');
    this.renderer.addClass(controlRow, 'glo-container');
    this.renderer.setStyle(controlRow, 'display', 'flex');
    this.renderer.setStyle(controlRow, 'align-items', 'center');
    this.renderer.setStyle(controlRow, 'gap', '4px');

    this.renderer.insertBefore(parent, container, inputEl);
    this.renderer.appendChild(container, controlRow);
    this.renderer.appendChild(controlRow, inputEl);

    // 3. Button & Icon setup
    const button = this.renderer.createElement('button');
    this.renderer.setAttribute(button, 'type', 'button');
    this.renderer.addClass(button, 'global-variable-button');

    this.icon = this.renderer.createElement('mat-icon');
    this.renderer.addClass(this.icon, 'material-icons');
    this.renderer.addClass(this.icon, 'mat-ligature-font');
    const iconText = this.renderer.createText(this.glo ? 'delete' : 'language');
    this.renderer.appendChild(this.icon, iconText);

    this.renderer.appendChild(button, this.icon);
    this.renderer.appendChild(controlRow, button);

    // 4. Subtext Label (Positioned directly underneath the input)
    this.nameElement = this.renderer.createElement('span');
    this.renderer.addClass(this.nameElement, 'global-variable-name');
    this.renderer.setStyle(this.nameElement, 'font-size', '10px');
    this.renderer.setStyle(this.nameElement, 'color', '#666');
    this.renderer.setStyle(this.nameElement, 'line-height', '12px');
    this.renderer.setStyle(this.nameElement, 'margin-top', '-15px');
    this.renderer.setStyle(this.nameElement, 'white-space', 'nowrap');
    this.renderer.setStyle(this.nameElement, 'overflow', 'hidden');
    this.renderer.setStyle(this.nameElement, 'text-overflow', 'ellipsis');
    this.renderer.setStyle(this.nameElement, 'max-width', '140px'); // Prevents grid distortion

    this.renderer.setProperty(this.nameElement, 'textContent', this.glo?.name || '');
    this.renderer.appendChild(container, this.nameElement);

    // 5. Listener logic
    this.renderer.listen(button, 'click', () => {
      if (this.glo) {
        const index = this.glo.connections.findIndex((g: any) => g.documentId === targetId && g.field === this.fieldName);
        if (index !== -1) {
          this.glo.connections.splice(index, 1);
        }
        this.varService.patch(this.glo._id.toString(), { connections: this.glo.connections });
        this.glo = undefined;
        this.renderer.setProperty(this.icon, 'textContent', 'language');
        this.renderer.setProperty(this.nameElement, 'textContent', '');
      } else {
        this.createValue().then((newValue) => {
          this.setNestedValue(this.targetObject, this.fieldName, newValue);
          this.renderer.setProperty(this.nameElement, 'textContent', this.glo?.name || '');
        });
      }
    });
  }

  private async createValue(): Promise<any> {
    const dialogRef: MatDialogRef<GlobalVariablesSelectComponent> = this.dialog.open(GlobalVariablesSelectComponent);
    const res = await firstValueFrom(dialogRef.afterClosed());

    if (!res) {
      return this.globalVariable;
    }
    if (!res.connections) {
      res.connections = [];
    }
    const targetId = this.targetObject?._id?.toString();
    await this.varService.patch(res._id.toString(), { connections: res.connections.concat([{ field: this.fieldName, documentId: targetId }]) });
    this.glo = res;
    this.renderer.setProperty(this.icon, 'textContent', 'delete');
    return this.getValue(res);
  }

  private getValue(glo: any) {
    switch (glo.type) {
      case 'string':
        return glo.value;
      case 'number':
        return parseInt(glo.value, 10);
      case 'date': case 'dayMonth':
        return new Date(glo.value);
      default:
        return glo.value;
    }
  }

  private setNestedValue(obj: any, path: string, value: any): void {
    if (!obj || !path) return;
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];

      if (current[key] === undefined || current[key] === null) {
        current[key] = !isNaN(Number(nextKey)) ? [] : {};
      }
      current = current[key];
    }
    current[keys[keys.length - 1]] = value;
  }
}
